// Derives the Palmtop Backlog Manifest view model (KPIs, risk briefing with a
// paired action per risk) from the static snapshot in constants/palmtopDashboard.js.
//
// The briefing is rule-based: every sentence is a fixed template filled from
// the numbers, so it can never state something the data doesn't support.
// Text uses **double asterisks** to mark emphasised figures; the dashboard
// renders those segments in bold.

import { PALMTOP_DATA, PALMTOP_META } from '../constants/palmtopDashboard';

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// A month counts as "backlogged" once its rate crosses this line; below it the
// overdue quantity is rounding noise.
const SHIFT_THRESHOLD_PCT = 1;
const AGED_BUCKET = '180+ days';

// ---------- formatting ----------

export const fmtNum = (n, digits = 0) =>
  Number(n).toLocaleString('en-US', { maximumFractionDigits: digits });

export const fmtCompact = (n) => {
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(2).replace(/\.00$/, '')}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1).replace(/\.0$/, '')}K`;
  return fmtNum(n);
};

export const fmtPct = (v, digits = 1) => `${Number(v.toFixed(digits))}%`;

export const ymShort = (ym) => {
  const [y, m] = ym.split('-');
  return `${MONTHS_SHORT[+m - 1]} '${y.slice(2)}`;
};

export const ymLong = (ym) => {
  const [y, m] = ym.split('-');
  return `${MONTHS_LONG[+m - 1]} ${y}`;
};

export const fmtDate = (iso) => {
  const [y, m, d] = iso.split('-');
  return `${+d} ${MONTHS_SHORT[+m - 1]} ${y}`;
};

const titleCase = (s) =>
  s.toLowerCase().replace(/\b([a-z])/g, (c) => c.toUpperCase());

// "20LITRES NET YELLOW JERRYCAN" -> "20L yellow jerrycan".
export const packingLabel = (s) =>
  s
    .replace(/(\d+)\s*LITRES?\s*(NET\s*)?/gi, '$1L ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/(\d+)l\b/g, '$1L');

const listNames = (names) =>
  names.length <= 1
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

// ---------- derivation ----------

export const buildPalmtopAnalytics = () => {
  const d = PALMTOP_DATA;
  const pct = (part, whole) => (whole ? (part / whole) * 100 : 0);

  // Trend: find the month the backlog rate stepped up.
  const trend = d.monthly_trend;
  const shiftIdx = trend.findIndex((m) => m.rate_pct >= SHIFT_THRESHOLD_PCT);
  const pre = shiftIdx > 0 ? trend.slice(0, shiftIdx) : [];
  const post = shiftIdx >= 0 ? trend.slice(shiftIdx) : trend;
  const shift = trend[shiftIdx] || trend[0];
  const preMax = pre.length ? Math.max(...pre.map((m) => m.rate_pct)) : 0;
  // Smallest 0.05 step strictly above the pre-shift maximum ("under 0.15%").
  const preCeil = (Math.floor(preMax / 0.05) + 1) * 0.05;
  const postMin = Math.min(...post.map((m) => m.rate_pct));
  const postMax = Math.max(...post.map((m) => m.rate_pct));
  const latest = trend[trend.length - 1];
  const peakRate = trend.reduce((a, m) => (m.rate_pct > a.rate_pct ? m : a), trend[0]);
  const peakPlanned = trend.reduce((a, m) => (m.planned_qty > a.planned_qty ? m : a), trend[0]);

  // Concentration.
  const wh = d.warehouse_risk;
  const whTotal = wh.reduce((a, w) => a + w.v, 0);
  const whMain = wh[0];
  const whMainPct = pct(whMain.v, whTotal);
  const whOthers = wh.slice(1);
  const whOthersQty = whOthers.reduce((a, w) => a + w.v, 0);

  const aged = d.aging_buckets.find((b) => b.bucket === AGED_BUCKET) || { n: d.aged_rows, v: d.aged_qty };
  const agedQtyPct = pct(aged.v, d.atrisk_qty);
  const agedRowPct = pct(aged.n, d.atrisk_rows);

  const openSharePct = pct(d.atrisk_qty, d.open_backlog_qty);

  const top3Dest = d.top_destinations.slice(0, 3);
  const top3Cust = d.top_customers.slice(0, 3);
  const topMkt = d.top_marketers[0];
  const top3DestPct = pct(top3Dest.reduce((a, x) => a + x.v, 0), d.atrisk_qty);
  const top3CustPct = pct(top3Cust.reduce((a, x) => a + x.v, 0), d.atrisk_qty);
  const topMktPct = pct(topMkt.v, d.atrisk_qty);

  // Freight terms: over/under-representation among terms with real volume.
  const terms = d.incoterm_compare
    .filter((t) => t.overall_pct >= 5)
    .map((t) => ({ ...t, ratio: t.overall_pct ? t.risk_pct / t.overall_pct : 0 }));
  const worstTerm = terms.reduce((a, t) => (t.ratio > a.ratio ? t : a), terms[0]);
  const safestTerm = terms.reduce((a, t) => (t.ratio < a.ratio ? t : a), terms[0]);

  // Packaging: the main site's product mix explains most of the jerrycan skew.
  const pkg = d.packaging_summary;
  const topFormat = d.packaging_formats[0];

  // Pipeline-wide measures (production backlog, late to port).
  const pb = d.production_backlog;
  const pbTop = pb.by_warehouse[0];
  const stageOf = (name) => d.pipeline.find((s) => s.stage === name) || { n: 0, overdue_n: 0 };
  const noBooking = stageOf('Instruction, no booking');
  const packed = stageOf('Packed');
  const plannedLines = d.pipeline
    .filter((s) => s.stage !== 'Contract only')
    .reduce((a, s) => a + s.n, 0);
  const ltp = d.late_to_port;
  const ltpWorst = ltp.by_warehouse[0];
  const ltpBest = ltp.by_warehouse[ltp.by_warehouse.length - 1];

  // ---------- KPIs ----------
  const packingKpis = [
    {
      label: 'At-risk volume',
      value: fmtNum(d.atrisk_qty),
      sub: `units across ${fmtNum(d.atrisk_rows)} shipments, unpacked past due date`,
      tone: 'critical',
    },
    {
      label: 'Overdue share of open backlog',
      value: fmtNum(d.atrisk_qty),
      sub: `of ${fmtNum(d.open_backlog_qty)} unpacked units are already overdue (${fmtPct(openSharePct)})`,
      tone: 'critical',
    },
    {
      label: 'Aged 180+ days',
      value: fmtNum(aged.v),
      sub: `units across ${fmtNum(aged.n)} of ${fmtNum(d.atrisk_rows)} shipments (${fmtPct(agedQtyPct, 0)} of at-risk qty)`,
      tone: 'serious',
    },
    {
      label: `At risk at ${whMain.si_warehouse}`,
      value: fmtNum(whMain.v),
      sub: `units of ${fmtNum(whTotal)} (${fmtPct(whMainPct)}), ${fmtNum(whMain.n)} of ${fmtNum(d.atrisk_rows)} shipments`,
      tone: 'serious',
    },
    {
      label: `Overdue in ${ymShort(latest.ym)}`,
      value: fmtNum(latest.atrisk_qty),
      sub: `units of ${fmtNum(latest.planned_qty)} planned that month (${fmtPct(latest.rate_pct)}; under ${fmtPct(preCeil, 2)} before ${ymShort(shift.ym)})`,
      tone: 'warning',
    },
    {
      label: 'Top-3 customers',
      value: fmtNum(d.top3_customer_qty),
      sub: `units of ${fmtNum(d.atrisk_qty)} (${fmtPct(top3CustPct)}), 3 of ${fmtNum(PALMTOP_META.totalCustomers)} customers`,
      tone: 'neutral',
    },
    {
      label: 'Containers affected',
      value: fmtNum(d.containers.fcl),
      sub: `FCL on the ${fmtNum(d.containers.si)} shipping instructions (${fmtNum(d.containers.sc)} contracts) with stuck qty`,
      tone: 'serious',
    },
    {
      label: `Jerrycans at ${pkg.warehouse}`,
      value: fmtNum(pkg.jerrycan_at_main_qty),
      sub: `units of ${fmtNum(d.atrisk_qty)} at-risk volume (${fmtPct(pkg.jerrycan_at_main_pct)}) are jerrycans at this one site`,
      tone: 'serious',
    },
  ];

  const pipelineKpis = [
    {
      label: 'Production backlog',
      value: fmtNum(pb.n),
      sub: `booked lines with no production order, past required date — median ${fmtNum(pb.median_days)} days overdue`,
      tone: 'critical',
    },
    {
      label: 'Awaiting booking',
      value: fmtNum(noBooking.overdue_n),
      sub: `of ${fmtNum(noBooking.n)} shipping-instruction lines with no booking are past required date`,
      tone: 'serious',
    },
    {
      label: 'Late to port',
      value: fmtNum(ltp.late_n),
      sub: `of ${fmtNum(ltp.n)} packed lines reached port after required date (${fmtPct(ltp.late_pct)}) — median ${ltp.median_slip_late} days late`,
      tone: 'warning',
    },
    {
      label: 'Packed',
      value: fmtNum(packed.n),
      sub: `of ${fmtNum(plannedLines)} planned lines fully packed (${fmtPct(pct(packed.n, plannedLines))})`,
      tone: 'good',
    },
  ];

  const kpiGroups = [
    {
      title: 'Packing backlog',
      caption: 'In production, quantity still unpacked, required date passed',
      items: packingKpis,
    },
    {
      title: 'Across the pipeline',
      caption: 'Every master-plan line, from shipping instruction to packed',
      items: pipelineKpis,
    },
  ];

  // ---------- briefing ----------
  // Each risk carries the one action that addresses it, so the dashboard can
  // show them side by side.
  const findings = [
    {
      severity: postMin > preMax * 10 ? 'Critical' : 'Serious',
      title: 'A regime shift, not noise',
      text:
        `Backlog rate sat under **${fmtPct(preCeil, 2)}** of planned volume every month through ` +
        `${ymLong(pre.length ? pre[pre.length - 1].ym : shift.ym)}. From **${ymLong(shift.ym)}** it jumped to a sustained ` +
        `**${fmtPct(postMin)}–${fmtPct(postMax)}** and has stayed there for **${post.length} straight months** ` +
        `(${fmtNum(shift.atrisk_qty)} units overdue of ${fmtNum(shift.planned_qty)} planned in that first month alone). ` +
        'That step change points to a structural capacity problem, not month-to-month variance.',
      action: {
        lead: `Find what changed at ${whMain.si_warehouse} in ${ymLong(shift.ym)}`,
        rest: '— staffing, line, packaging supplier or process — and reverse it; a step change this sharp has a single cause.',
      },
    },
    {
      severity: peakRate.ym > peakPlanned.ym ? 'Critical' : 'Serious',
      title: "Lower volume hasn't fixed it",
      text:
        `Planned volume has fallen from its ${ymShort(peakPlanned.ym)} peak (**${fmtNum(peakPlanned.planned_qty)}** → ` +
        `**${fmtNum(latest.planned_qty)}** by ${ymShort(latest.ym)}), yet the backlog rate hit its **highest point** ` +
        `in ${ymLong(peakRate.ym)} (**${fmtNum(peakRate.atrisk_qty)}** of ${fmtNum(peakRate.planned_qty)} units, ${fmtPct(peakRate.rate_pct)}). Shrinking throughput is exposing the bottleneck, not relieving it.`,
      action: {
        lead: `Don't cut ${whMain.si_warehouse} capacity in line with falling volume`,
        rest: `until the backlog rate is back under ${SHIFT_THRESHOLD_PCT}% — the spare capacity is needed to clear the overdue queue.`,
      },
    },
    {
      severity: 'Critical',
      title: 'A second, larger backlog before production',
      text:
        `**${fmtNum(pb.n)}** booked lines have **no production order** and are already past their required date — ` +
        `${(pb.n / d.atrisk_rows).toFixed(1)}× the packing backlog by line count, across ${fmtNum(pb.contracts)} contracts. ` +
        `The median line is **${fmtNum(pb.median_days)} days** overdue, **${fmtNum(pb.aged_n)}** are past 180 days, ` +
        `and the planned quantity behind them is **${fmtNum(pb.qty)}** units. ` +
        `**${pbTop.name}** alone accounts for ${fmtNum(pbTop.n)}.`,
      action: {
        lead: `Reconcile the ${fmtNum(pb.n)} booked-but-unscheduled lines with planning`,
        rest: `— raise production orders for live ones and close cancelled bookings, starting with ${pbTop.name} (${fmtNum(pbTop.n)} lines).`,
      },
    },
    {
      severity: whMainPct >= 75 ? 'Serious' : 'Warning',
      title: 'One warehouse, one problem',
      text:
        `**${whMain.si_warehouse}** holds **${fmtNum(whMain.v)}** of ${fmtNum(whTotal)} at-risk units ` +
        `(${fmtPct(whMainPct)}) across ${fmtNum(whMain.n)} of ${fmtNum(d.atrisk_rows)} shipments. ` +
        'This is a single-site operational failure, not a network-wide one — fix targeted there first.',
      action: {
        lead: `Run a capacity/process review at ${whMain.si_warehouse} specifically`,
        rest: `— the other ${whOthers.length} warehouses show almost no backlog, so the fix is local, not company-wide.`,
      },
    },
    {
      severity: pkg.jerrycan_at_main_pct >= 50 ? 'Serious' : 'Warning',
      title: `It's a jerrycan-filling problem at ${pkg.warehouse}`,
      text:
        `**${fmtNum(pkg.jerrycan_at_main_qty)}** of ${fmtNum(d.atrisk_qty)} at-risk units (${fmtPct(pkg.jerrycan_at_main_pct)}) ` +
        `are jerrycans at ${pkg.warehouse}, a site where jerrycans are ${fmtPct(pkg.main_jerrycan_planned_pct, 0)} of planned volume. ` +
        `One format — **${packingLabel(topFormat.name)}** — accounts for **${fmtNum(topFormat.v)}** units across ${fmtNum(topFormat.n)} lines.`,
      action: {
        lead: `Check jerrycan supply and filling-line capacity at ${pkg.warehouse}`,
        rest: `— start with ${packingLabel(topFormat.name)} (${fmtNum(topFormat.n)} lines, ${fmtNum(topFormat.v)} units outstanding).`,
      },
    },
    {
      severity: agedQtyPct >= 50 ? 'Serious' : 'Warning',
      title: `${agedRowPct >= 60 ? 'Nearly two-thirds' : 'A large share'} is aged past 180 days`,
      text:
        `**${fmtNum(aged.n)}** at-risk shipments (**${fmtNum(aged.v)}** units) have sat unpacked for over six months. ` +
        'These are unlikely to still be "in progress" — they need a case-by-case audit to split recoverable orders from ones to cancel or write off.',
      action: {
        lead: `Audit the ${fmtNum(aged.n)} shipments aged 180+ days at ${whMain.si_warehouse}`,
        rest: 'this week — tag each as recoverable-and-expedite or cancel/write-off.',
      },
    },
    {
      severity: 'Warning',
      title: 'Freight terms correlate with getting stuck',
      text:
        `**${worstTerm.name}** covers ${fmtNum(worstTerm.overall_n)} of ${fmtNum(worstTerm.overall_total_n)} shipments ` +
        `(${fmtPct(worstTerm.overall_pct, 0)}) but **${fmtNum(worstTerm.risk_v)}** of ${fmtNum(d.atrisk_qty)} at-risk units ` +
        `(${fmtPct(worstTerm.risk_pct)}) — ${worstTerm.ratio.toFixed(2)}× over-represented. **${safestTerm.name}** is the safest: ` +
        `${fmtNum(safestTerm.overall_n)} shipments but only ${fmtNum(safestTerm.risk_v)} at-risk units.`,
      action: {
        lead: `Review freight-arrangement steps for ${worstTerm.name} bookings`,
        rest: `before quoting new ${worstTerm.name} business, given their disproportionate share of stuck orders.`,
      },
    },
    {
      severity: 'Warning',
      title: 'Even packed cargo usually reaches port late',
      text:
        `**${fmtNum(ltp.late_n)}** of ${fmtNum(ltp.n)} packed lines (${fmtPct(ltp.late_pct)}) reached port after their ` +
        `required date, typically **${ltp.median_slip_late} days** late. It is network-wide, not one site: ` +
        `${fmtNum(ltpBest.late_n)} of ${fmtNum(ltpBest.n)} at ${ltpBest.name} (${fmtPct(ltpBest.late_pct)}) up to ` +
        `**${fmtNum(ltpWorst.late_n)}** of ${fmtNum(ltpWorst.n)} at ${ltpWorst.name} (${fmtPct(ltpWorst.late_pct)}).`,
      action: {
        lead: 'Compare required dates against real port cut-off lead times',
        rest: `— a consistent ~${ltp.median_slip_late}-day slip at every site suggests dates are set too tight or haulage is the bottleneck, starting with ${ltpWorst.name}.`,
      },
    },
    {
      severity: 'Informational',
      title: 'The risk is a short, actionable list',
      text:
        `Three destinations (${listNames(top3Dest.map((x) => titleCase(x.name)))}) hold **${fmtNum(d.top3_dest_qty)}** of ` +
        `${fmtNum(d.atrisk_qty)} at-risk units (${fmtPct(top3DestPct, 0)}); three customers hold ` +
        `**${fmtNum(d.top3_customer_qty)}** (${fmtPct(top3CustPct, 0)}); one marketer, **${titleCase(topMkt.name)}**, ` +
        `is tied to **${fmtNum(topMkt.v)}** (${fmtPct(topMktPct)}) across ${fmtNum(topMkt.n)} shipments. ` +
        'This is a targeted account-management problem, not a diffuse one.',
      action: {
        lead: 'Set a standing weekly check',
        rest:
          `on ${listNames(top3Dest.map((x) => titleCase(x.name)))} shipments and the ` +
          `${listNames(top3Cust.map((x) => titleCase(x.name)))} accounts, which drive most of the concentration risk.`,
      },
    },
  ];

  return {
    meta: PALMTOP_META,
    data: d,
    kpiGroups,
    findings,
    trend: {
      rows: trend,
      shiftYm: shift.ym,
      yMax: Math.ceil(postMax / 4) * 4,
    },
    concentration: {
      main: whMain,
      mainPct: whMainPct,
      others: whOthers,
      othersQty: whOthersQty,
    },
  };
};
